<template>
  <div class="bill-wrapper">
    <!-- 标题 -->
    <div class="title"> <el-text
      size="large"
    >{{ $t('views.account_center.bill') }}</el-text></div>
    <!-- 横线已整合到 title 样式中 -->
    <!-- 骨架加载 -->
    <div
      v-if="isLoading"
      class="table-skeleton"
    >
      <el-skeleton animated>
        <template #template>
          <!-- 表头骨架 -->
          <div class="skeleton-header">
            <el-skeleton-item
              v-for="n in 4"
              :key="'header' + n"
              variant="text"
              class="skeleton-header-item"
            />
          </div>
          <!-- 表格内容骨架 -->
          <div
            v-for="row in 3"
            :key="'row' + row"
            class="skeleton-row"
          >
            <el-skeleton-item
              v-for="col in 4"
              :key="'col' + col"
              variant="text"
              class="skeleton-cell"
            />
          </div>
        </template>
      </el-skeleton>
    </div>
    <!-- 表格 -->
    <el-table
      v-else
      :data="tableData"
      header-cell-class-name="table-header"
      class="bill-custom-table"
    >
      <el-table-column
        prop="date"
        :label="$t('views.account_center.date')"
        width="150"
      />
      <el-table-column
        prop="name"
        :label="$t('views.account_center.plan_name')"
      >
        <template #default="scope">
          {{ scope.row.name }}<span
            v-if="scope.row.order_status !== 1"
          ><el-text
            class="mx-1"
            type="danger"
          >{{ $t('views.account_center.unpaid') }}</el-text></span>
        </template>
      </el-table-column>
      <el-table-column
        :label="$t('views.account_center.amount')"
        width="200"
      >
        <template #default="scope">
          {{ formatOrderAmount(scope.row) }}
        </template>
      </el-table-column>
      <el-table-column
        :label="$t('views.account_center.invoice')"
        width="240"
      >
        <template #default="scope">
          <div class="invoice-actions">
            <div class="invoice-action-slot">
              <el-button
                v-if="isStripeOrder(scope.row) && scope.row.order_status === 1"
                class="mx-1 down-PDF"
                type="primary"
                link
                :loading="scope.row.loading"
                @click="getInvoicePdf(scope.row)"
              >
                {{ scope.row.order_type === 2 ? $t('views.account_center.view_receipt') : $t('views.account_center.download_pdf') }}
              </el-button>
              <el-button
                v-else-if="!isStripeOrder(scope.row) && isInvoiceIssued(scope.row)"
                class="invoice-status-button"
                type="primary"
                link
                :loading="scope.row.invoiceLoading"
                @click="downloadDomesticInvoice(scope.row)"
              >下载发票</el-button>
              <el-button
                v-else-if="!isStripeOrder(scope.row) && canOpenInvoiceDialog(scope.row)"
                class="invoice-status-button"
                :type="invoiceStatusType(scope.row)"
                link
                @click="openInvoiceDialog(scope.row)"
              >{{ invoiceStatusText(scope.row) }}</el-button>
            </div>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog
      v-model="invoiceDialogVisible"
      class="invoice-dialog"
      width="920px"
      :show-close="true"
      append-to-body
    >
      <template #header>
        <div class="invoice-dialog-title">
          <span>{{ invoiceDialogTitle }}</span>
          <span class="invoice-dialog-tip">（该页面为普票开具入口）</span>
          <!-- ，专票开具流程 <el-link type="primary" :underline="false" href="#" @click.prevent="showSpecialInvoiceTip">查看这里</el-link> -->
        </div>
      </template>

      <div class="invoice-summary">
        <div class="invoice-summary-item"><span>开票金额</span><strong>{{ selectedInvoiceRow ? formatOrderAmount(selectedInvoiceRow) : '-' }}</strong></div>
        <div class="invoice-summary-item"><span>付款时间</span><b>{{ selectedInvoiceRow && (selectedInvoiceRow.payment_time || selectedInvoiceRow.date) || '-' }}</b></div>
        <div class="invoice-summary-item"><span>订阅内容</span><b>{{ selectedInvoiceRow && selectedInvoiceRow.name || '-' }}</b></div>
        <div class="invoice-summary-item"><span>发票类型</span><b>数字化电子普票</b></div>
        <div class="invoice-summary-item invoice-summary-taxpayer"><span>单位类型</span><div class="invoice-taxpayer-switch"><button :class="{ active: invoiceForm.taxpayer_type === 'enterprise' }" :disabled="invoiceFormReadOnly" type="button" @click="invoiceForm.taxpayer_type = 'enterprise'">企业</button><button :class="{ active: invoiceForm.taxpayer_type === 'personal' }" :disabled="invoiceFormReadOnly" type="button" @click="invoiceForm.taxpayer_type = 'personal'">个人</button></div></div>
        <div class="invoice-summary-item"><span>开票状态</span><b>{{ invoiceStatusText(selectedInvoiceRow) }}</b></div>
      </div>

      <el-divider />

      <div v-if="isInvoiceRejected(selectedInvoiceRow)" class="invoice-reject-alert">
        <strong>驳回原因</strong>
        <span>{{ invoiceRejectReason(selectedInvoiceRow) }}</span>
      </div>

      <el-form ref="invoiceFormRef" v-loading="invoiceDetailLoading" :model="invoiceForm" :class="{ 'invoice-form-readonly': invoiceFormReadOnly }" class="invoice-form" label-position="left">
        <el-form-item :class="{ 'invoice-form-full': invoiceForm.taxpayer_type === 'enterprise' }">
          <template #label><span class="invoice-field-label"><span class="invoice-required-mark">*</span>发票抬头</span></template>
          <el-input v-model="invoiceForm.invoice_title" :placeholder="invoiceFormReadOnly ? '-' : (invoiceForm.taxpayer_type === 'enterprise' ? '请输入企业单位的名称' : '请输入个人姓名')" :readonly="invoiceFormReadOnly" maxlength="255" />
        </el-form-item>
        <el-form-item v-if="invoiceForm.taxpayer_type === 'enterprise'">
          <template #label><span class="invoice-field-label"><span class="invoice-required-mark">*</span>单位税号</span></template>
          <el-input v-model="invoiceForm.tax_no" :placeholder="invoiceFormReadOnly ? '-' : '请输入企业单位的税号或社会信用代码'" :readonly="invoiceFormReadOnly" maxlength="64" />
        </el-form-item>
        <el-form-item>
          <template #label><span class="invoice-field-label"><span class="invoice-required-mark">*</span>联系邮箱</span></template>
          <el-input v-model="invoiceForm.email" :placeholder="invoiceFormReadOnly ? '-' : '请输入接收发票的联系邮箱'" :readonly="invoiceFormReadOnly" maxlength="255" />
        </el-form-item>
        <el-form-item>
          <template #label><span class="invoice-field-label"><span class="invoice-required-mark invoice-required-mark-hidden">*</span>{{ invoiceForm.taxpayer_type === 'enterprise' ? '注册地址' : '联系地址' }}</span></template>
          <el-input v-model="invoiceForm.registered_address" :placeholder="invoiceFormReadOnly ? '-' : (invoiceForm.taxpayer_type === 'enterprise' ? '请输入企业注册地址' : '请输入联系地址（选填）')" :readonly="invoiceFormReadOnly" maxlength="500" />
        </el-form-item>
        <el-form-item>
          <template #label><span class="invoice-field-label"><span class="invoice-required-mark invoice-required-mark-hidden">*</span>{{ invoiceForm.taxpayer_type === 'enterprise' ? '注册电话' : '联系电话' }}</span></template>
          <el-input v-model="invoiceForm.registered_phone" :placeholder="invoiceFormReadOnly ? '-' : (invoiceForm.taxpayer_type === 'enterprise' ? '请输入企业注册电话' : '请输入联系电话（选填）')" :readonly="invoiceFormReadOnly" maxlength="64" />
        </el-form-item>
        <el-form-item>
          <template #label><span class="invoice-field-label"><span class="invoice-required-mark invoice-required-mark-hidden">*</span>开户银行</span></template>
          <el-input v-model="invoiceForm.bank_name" :placeholder="invoiceFormReadOnly ? '-' : '请输入开户银行（选填）'" :readonly="invoiceFormReadOnly" maxlength="255" />
        </el-form-item>
        <el-form-item>
          <template #label><span class="invoice-field-label"><span class="invoice-required-mark invoice-required-mark-hidden">*</span>开户账号</span></template>
          <el-input v-model="invoiceForm.bank_account" :placeholder="invoiceFormReadOnly ? '-' : '请输入银行账号（选填）'" :readonly="invoiceFormReadOnly" maxlength="128" />
        </el-form-item>
        <el-form-item class="invoice-form-full"><template #label><span class="invoice-field-label"><span class="invoice-required-mark invoice-required-mark-hidden">*</span>发票备注</span></template><el-input v-model="invoiceForm.remark" :placeholder="invoiceFormReadOnly ? '-' : '请输入发票备注（选填）'" :readonly="invoiceFormReadOnly" type="textarea" :rows="2" maxlength="500" /></el-form-item>
      </el-form>

      <p class="invoice-privacy"><span>*</span>上述开票信息将仅用于为您提供开票服务，我们承诺将依据相关法律法规要求妥善处理您的个人信息。</p>
      <template #footer>
        <el-button v-if="canSubmitInvoice" class="invoice-submit-button" type="primary" :loading="invoiceSubmitting" :disabled="invoiceDetailLoading" @click="submitInvoice">{{ isInvoiceRejected(selectedInvoiceRow) ? '重新提交' : '提交信息' }}</el-button>
        <template v-if="isInvoiceIssued(selectedInvoiceRow)">
          <el-button class="invoice-submit-button invoice-download-button" type="primary" plain :loading="selectedInvoiceRow && selectedInvoiceRow.invoiceLoading" :disabled="invoiceDetailLoading" @click="downloadDomesticInvoice(selectedInvoiceRow)">下载发票</el-button>
        </template>
      </template>
    </el-dialog>

    <!-- 分页器 -->
    <el-pagination
      :current-page="pageNum"
      :page-sizes="[5, 10]"
      :page-size="pageSize"
      layout="total,prev, pager, next, sizes,jumper"
      :total="total"
      class="custom-pagination"
      size="small"
      :pager-count="5"
      @size-change="handleSizeChange"
      @current-change="handleCurrentChange"
    />
  </div>
</template>

<script>
import { getOrderList, downInvoicePdf, applyInvoice, getInvoiceDetail, downloadInvoice } from '@/api/order'
import { formatCurrency } from '@/utils/currency'

const createInvoiceForm = (orderId = null, detail = {}) => ({
  order_id: orderId,
  invoice_type: detail.invoice_type || 'vat_electronic_normal',
  taxpayer_type: detail.taxpayer_type || 'enterprise',
  invoice_title: detail.invoice_title || '',
  tax_no: detail.tax_no || '',
  email: detail.email || '',
  registered_address: detail.registered_address || '',
  registered_phone: detail.registered_phone || '',
  bank_name: detail.bank_name || '',
  bank_account: detail.bank_account || '',
  remark: detail.remark || ''
})
const INVOICE_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const INVOICE_PHONE_PATTERN = /^(1[3-9]\d{9}|0\d{2,3}-?\d{7,8})$/
const INVOICE_TAX_NO_PATTERN = /^(?:[0-9A-Z]{18}|\d{15})$/

export default {
  data() {
    return {
      tableData: [],
      pageNum: 1,
      pageSize: 10,
      total: 0,
      isLoading: true // 骨架加载
      , invoiceDialogVisible: false
      , invoiceSubmitting: false
      , invoiceDetailLoading: false
      , selectedInvoiceRow: null
      , invoiceDetail: null
      , invoiceForm: createInvoiceForm()
    }
  },
  computed: {
    invoiceFormReadOnly() {
      return Boolean(this.selectedInvoiceRow && !this.hasNoInvoiceApplication(this.selectedInvoiceRow) && !this.isInvoiceRejected(this.selectedInvoiceRow))
    },
    canSubmitInvoice() {
      return Boolean(this.selectedInvoiceRow && (this.hasNoInvoiceApplication(this.selectedInvoiceRow) || this.isInvoiceRejected(this.selectedInvoiceRow)))
    },
    invoiceDialogTitle() {
      if (this.isInvoiceRejected(this.selectedInvoiceRow)) return '重新申请开票'
      return this.hasNoInvoiceApplication(this.selectedInvoiceRow) ? '申请开票' : '开票申请详情'
    }
  },
  mounted() {
    this.getOrderList()
  },
  methods: {
    isStripeOrder(row) {
      return Boolean(row && row.payment_channel === 'stripe')
    },
    canOpenInvoiceDialog(row) {
      return Boolean(row && (!this.hasNoInvoiceApplication(row) || row.order_status === 1))
    },
    isInvoiceProcessing(row) {
      return row && (row.invoice_status === 0 || row.invoice_status === 1)
    },
    isInvoiceRejected(row) {
      return Boolean(row && row.invoice_status === 3)
    },
    isInvoiceIssued(row) {
      return Boolean(row && row.invoice_status === 2)
    },
    hasNoInvoiceApplication(row) {
      return row && (row.invoice_status === null || typeof row.invoice_status === 'undefined')
    },
    invoiceRejectReason(row) {
      return String(this.invoiceDetail && this.invoiceDetail.reject_reason || row && row.invoice_reject_reason || '请根据驳回提示修改后重新提交')
    },
    invoiceStatusType(row) {
      if (this.isInvoiceProcessing(row)) return 'warning'
      if (this.isInvoiceRejected(row)) return 'danger'
      if (this.isInvoiceIssued(row)) return 'success'
      if (row && row.invoice_status === 4) return 'info'
      return 'primary'
    },
    formatOrderAmount(row) {
      return formatCurrency(row.amount, row.currency)
    },
    getOrderList() {
      this.isLoading = true
      const params = {
        page: this.pageNum,
        page_size: this.pageSize
      }
      getOrderList(params)
        .then(response => {
          if (response) {
            this.tableData = response.data.data
            this.total = response.data.total
          }
        })
        .catch(error => {
          console.error('加载账单失败:', error)
        })
        .finally(() => {
          this.isLoading = false // 加载完成
        })
    },
    getInvoicePdf(row) {
      // 设置当前行的loading状态
      row.loading = true

      downInvoicePdf({ order_id: row.id })
        .then(response => {
          if (response.code === 0 && response.data.has_invoice) {
            if (response.data.type === 'Receipt') {
              // Receipt 类型：打开新窗口查看
              window.open(response.data.invoice_pdf, '_blank')
            } else {
              // Invoice 类型：下载PDF
              this.$message({
                message: this.$t('views.account_center.downloading'),
                type: 'success',
                duration: 2000
              })
              this.$common.download(response.data.invoice_pdf)
            }
          }
        })
        .catch(error => {
          console.error('获取发票失败:', error)
        })
        .finally(() => {
          row.loading = false
        })
    },
    openInvoiceDialog(row) {
      this.selectedInvoiceRow = row
      this.invoiceDetail = null
      this.invoiceDetailLoading = false
      this.invoiceForm = createInvoiceForm(row.id)
      this.invoiceDialogVisible = true
      if (this.hasNoInvoiceApplication(row)) return

      this.invoiceDetailLoading = true
      const params = row.invoice_application_id ? { id: row.invoice_application_id } : { order_id: row.id }
      getInvoiceDetail(params).then(response => {
        this.invoiceDetail = response.data || {}
        this.invoiceForm = createInvoiceForm(row.id, this.invoiceDetail)
      }).catch(() => {
        // 请求层已提示错误，在页面消费拒绝，允许用户继续操作。
      }).finally(() => {
        this.invoiceDetailLoading = false
      })
    },
    invoiceStatusText(row) {
      if (!row || row.invoice_status === null || typeof row.invoice_status === 'undefined') return '申请开票'
      return { 0: '开票中', 1: '开票中', 2: '已开票', 3: '已驳回', 4: '已取消' }[row.invoice_status] || '申请开票'
    },
    showSpecialInvoiceTip() {
      this.$message.info('专用发票请联系运营管理员处理')
    },
    submitInvoice() {
      const email = String(this.invoiceForm.email || '').trim()
      const phone = String(this.invoiceForm.registered_phone || '').trim()
      const taxNo = String(this.invoiceForm.tax_no || '').trim().toUpperCase()
      if (!this.invoiceForm.invoice_title || !email) {
        this.$message.error('请填写发票抬头和联系邮箱')
        return
      }
      if (!INVOICE_EMAIL_PATTERN.test(email)) {
        this.$message.error('请输入正确的邮箱地址')
        return
      }
      if (phone && !INVOICE_PHONE_PATTERN.test(phone)) {
        this.$message.error('请输入正确的联系电话')
        return
      }
      if (this.invoiceForm.taxpayer_type === 'enterprise' && !taxNo) {
        this.$message.error('企业抬头必须填写单位税号')
        return
      }
      if (taxNo && !INVOICE_TAX_NO_PATTERN.test(taxNo)) {
        this.$message.error('单位税号应为15位或18位数字/大写字母')
        return
      }
      this.invoiceForm.email = email
      this.invoiceForm.registered_phone = phone
      this.invoiceForm.tax_no = taxNo
      this.invoiceSubmitting = true
      applyInvoice(this.invoiceForm).then(() => {
        this.$message.success('开票申请已提交')
        this.invoiceDialogVisible = false
        this.getOrderList()
      }).catch(() => {
        // 请求层已提示错误，在页面消费拒绝，保留表单供用户重试。
      }).finally(() => { this.invoiceSubmitting = false })
    },
    downloadDomesticInvoice(row) {
      row.invoiceLoading = true
      downloadInvoice({ id: row.invoice_application_id }).then(response => {
        const data = response.data || {}
        if (data.url) this.$common.download(data.url)
      }).catch(() => {
        // 请求层已提示错误，在页面消费拒绝，允许用户重试下载。
      }).finally(() => { row.invoiceLoading = false })
    },
    handleSizeChange(val) {
      this.pageSize = val
      this.pageNum = 1
      this.getOrderList()
    },
    handleCurrentChange(val) {
      this.pageNum = val
      this.getOrderList()
    }
  }
}
</script>
<style lang="scss">
@import '@/assets/css/variables';
.bill-custom-table {
  .el-table__header-wrapper thead th {
    color: var(--agst-text-color-primary,$text-color-primary);
    text-align: center;
  }
  .el-table__body-wrapper tbody td {
    border: none;
    text-align: center;
    height: 40px;
  }
}
.invoice-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  width: 100%;
  white-space: nowrap;
}
.invoice-action-slot {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
}
.invoice-actions .el-button {
  margin-left: 0;
  margin-right: 0;
}
.invoice-status-button {
  min-width: 96px;
}
.invoice-status-text {
  line-height: 24px;
  white-space: nowrap;
}
.invoice-reject-alert {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin: -8px 0 24px;
  padding: 12px 16px;
  border: 1px solid #fbc4c4;
  border-radius: 6px;
  background: #fef0f0;
  color: #f56c6c;
  font-size: 14px;
  line-height: 22px;
}
.invoice-reject-alert strong {
  flex: 0 0 auto;
  color: #f23030;
}
.invoice-reject-alert span {
  min-width: 0;
  overflow-wrap: anywhere;
}

.invoice-dialog .el-dialog {
  border-radius: 18px;
  overflow: hidden;
}
.invoice-dialog .el-dialog__header {
  padding: 28px 32px 12px;
  margin-right: 0;
}
.invoice-dialog .el-dialog__body {
  padding: 0 32px 8px;
}
.invoice-dialog .el-dialog__footer {
  padding: 16px 32px 28px;
}
.invoice-dialog-title {
  color: #1f2937;
  font-size: 24px;
  font-weight: 700;
  line-height: 34px;
}
.invoice-dialog-tip {
  color: #1f2937;
  font-size: 14px;
  font-weight: 400;
  margin-left: 4px;
}
.invoice-dialog-tip .el-link {
  color: #7c4dce;
  vertical-align: baseline;
}
.invoice-summary {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 68px;
  row-gap: 20px;
  padding: 20px 0 18px;
}
.invoice-summary-item {
  display: flex;
  align-items: center;
  min-height: 28px;
  color: #9aa1ad;
  font-size: 16px;
}
.invoice-summary-item > span:first-child {
  flex: 0 0 84px;
}
.invoice-summary-item strong {
  color: #17191d;
  font-size: 22px;
}
.invoice-summary-item b {
  color: #292d33;
  font-weight: 400;
}
.invoice-summary-taxpayer {
  align-items: center;
}
.invoice-taxpayer-switch {
  display: flex;
  gap: 14px;
}
.invoice-taxpayer-switch button {
  border: 0;
  border-radius: 8px;
  background: #f4f4f5;
  color: #2d3138;
  cursor: pointer;
  font-size: 16px;
  padding: 10px 18px;
}
.invoice-taxpayer-switch button.active {
  background: #fff;
  border: 1px solid #252525;
  padding: 9px 17px;
}
.invoice-taxpayer-switch button:disabled {
  cursor: not-allowed;
  opacity: 0.65;
}
.invoice-dialog .el-divider {
  margin: 0 0 24px;
}
.invoice-form {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 42px;
}
.invoice-form .el-form-item {
  margin-bottom: 16px;
}
.invoice-form .el-form-item__label {
  box-sizing: border-box;
  color: #9aa1ad;
  font-size: 16px;
  padding-right: 10px;
  white-space: nowrap;
  width: 88px !important;
}
.invoice-form .el-form-item__content {
  margin-left: 0 !important;
}
.invoice-field-label {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}
.invoice-required-mark {
  display: inline-block;
  width: 10px;
  color: #f56c6c;
  line-height: 1;
  text-align: left;
}
.invoice-required-mark-hidden {
  visibility: hidden;
}
.invoice-form .invoice-form-full {
  grid-column: 1 / -1;
}
.invoice-form .el-input__wrapper,
.invoice-form .el-textarea__inner {
  background: #f5f6f8;
  border-radius: 8px;
  box-shadow: none;
}
.invoice-form .el-input__wrapper {
  box-sizing: border-box;
  height: 40px;
  min-height: 40px;
  padding: 0 12px;
}
.invoice-form .el-textarea__inner {
  padding: 10px 12px;
}
.invoice-form .el-input__inner,
.invoice-form .el-textarea__inner {
  color: #292d33;
  font-size: 16px;
}
.invoice-form .el-input__inner::placeholder,
.invoice-form .el-textarea__inner::placeholder {
  color: #a8adb7;
}
.invoice-form-readonly .el-input__wrapper,
.invoice-form-readonly .el-textarea__inner {
  background: #fff;
  border: 1px solid #ebeef5;
}
.invoice-privacy {
  color: #a3a9b4;
  font-size: 14px;
  line-height: 22px;
  margin: 2px 0 0;
}
.invoice-privacy span {
  color: #7c4dce;
  margin-right: 3px;
}
.invoice-cancel-button,
.invoice-submit-button {
  border-radius: 10px;
  font-size: 16px;
  height: 48px;
  min-width: 86px;
}
.invoice-submit-button {
  background: #252525;
  border-color: #252525;
  min-width: 120px;
}
.invoice-submit-button:hover,
.invoice-submit-button:focus {
  background: #111;
  border-color: #111;
}

/* 开票表单按桌面屏幕宽度适配输入框尺寸和行间距。 */
@media (min-width: 1360px) and (max-width: 1439px) {
  .invoice-form .el-form-item {
    margin-bottom: 20px;
  }
  .invoice-form .el-input__wrapper {
    padding-left: 14px;
    padding-right: 14px;
  }
  .invoice-form .el-textarea__inner {
    padding-left: 14px;
    padding-right: 14px;
  }
}
@media (min-width: 1440px) {
  .invoice-form .el-form-item {
    margin-bottom: 24px;
  }
  .invoice-form .el-input__wrapper {
    height: 44px;
    min-height: 44px;
    padding-left: 16px;
    padding-right: 16px;
  }
  .invoice-form .el-textarea__inner {
    padding-left: 16px;
    padding-right: 16px;
  }
}
</style>
<style scoped lang="scss">
@import '@/assets/css/views/account_center/components/bill';
</style>
